import React from 'react';
import { Box, Chip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { alpha } from '@mui/material/styles';
import { STATUS, TRANSLATION_STATUS } from './common';

// Chip tonal con el color de la paleta del tema: `primary`, `success`, `warning` o `grey`.
const toneSx = (color) => (theme) => {
    const main = color === 'grey' ? theme.palette.text.secondary : theme.palette[color].main;
    const text = color === 'grey' ? theme.palette.text.secondary : theme.palette[color].dark;
    return { bgcolor: alpha(main, 0.12), color: text, fontWeight: 500 };
};

export const StatusChip = ({ status }) => {
    const config = STATUS[status];
    if (!config) return null;
    return (
        <Chip
            size="small"
            label={config.label}
            sx={{ bgcolor: config.bg, color: config.fg, fontWeight: 500, fontSize: 12, borderRadius: 999, '& .MuiChip-label': { px: '10px' } }}
        />
    );
};

export const TranslationStatusChip = ({ status }) => {
    const config = TRANSLATION_STATUS[status];
    if (!config) return null;
    return <Chip size="small" label={config.label} sx={toneSx(config.color)} />;
};

const dotColor = (color) => (theme) => (color === 'grey' ? theme.palette.text.disabled : theme.palette[color].main);

// «Supera el límite» es un aro naranja y no un punto: «Sin traducir» ya es el punto naranja, y el
// color solo no los distinguía.
const dotSx = (color, ring = false) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    boxSizing: 'border-box',
    ...(ring ? { border: '2px solid', borderColor: dotColor(color), bgcolor: 'transparent' } : { bgcolor: dotColor(color) }),
});

/**
 * Punto de color con el estado de una traducción, para las pestañas de idioma: el chip las hacía
 * crecer en altura. El texto del estado va oculto a la vista pero sigue en el nombre accesible de
 * la pestaña, y el `title` lo muestra al pasar el ratón. La leyenda está en `TranslationStatusLegend`.
 */
export const TranslationStatusDot = ({ status, warning = false }) => {
    const config = TRANSLATION_STATUS[status];
    if (!config) return null;
    const label = warning ? `${config.label} · supera el límite` : config.label;
    return (
        <Box component="span" title={label} sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Box
                component="span"
                aria-hidden="true"
                sx={dotSx(warning ? 'warning' : config.color, warning)}
            />
            <Box component="span" sx={visuallyHidden}>
                {label}
            </Box>
        </Box>
    );
};

/**
 * Leyenda de los puntos de estado: solo los estados presentes en la publicación y, si alguna
 * traducción automática supera el límite, su aro naranja.
 */
export const TranslationStatusLegend = ({ statuses, withWarning = false }) => {
    const present = Object.keys(TRANSLATION_STATUS).filter((status) => Object.values(statuses || {}).includes(status));
    if (present.length === 0) return null;
    const items = present.map((status) => ({ key: status, color: TRANSLATION_STATUS[status].color, label: TRANSLATION_STATUS[status].label }));
    if (withWarning) items.push({ key: 'over', color: 'warning', ring: true, label: 'Supera el límite' });
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, color: 'text.secondary', fontSize: '12px' }}>
            {items.map((item) => (
                <Box key={item.key} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component="span" aria-hidden="true" sx={dotSx(item.color, item.ring)} />
                    {item.label}
                </Box>
            ))}
        </Box>
    );
};
